import React, { useState, useEffect } from "react";
import { X, Sparkles, Mail, Loader2, Copy, Check, Lock, Globe, Users } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "../lib/constants";
import * as api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function TaskModal({ task, activeCategory, onClose, onSave }) {
  const [formData, setFormData] = useState({
    category: activeCategory === "DASHBOARD" ? "TELEFONARE" : activeCategory,
    client: "", phone: "", email: "", description: "",
    entryDate: new Date().toISOString().split("T")[0],
    dueDate: "", amount: 0, paidAmount: 0,
    invoiceNumber: "", proformaNumber: "", invoiceDate: "",
    orderNumber: "", orderDate: "",
    offerNumber: "", offerDate: "", completed: false,
    visibility: "private", shared_with: [],
  });

  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const isOwner = !task || task._is_owner !== false;

  useEffect(() => {
    // Load users only if we may need sharing UI
    api.listUsers().then((list) => setUsers(list.filter((u) => u.id !== currentUser?.id))).catch(() => {});
  }, [currentUser?.id]);

  useEffect(() => {
    if (task) setFormData({ ...task });
  }, [task]);

  const [aiBusy, setAiBusy] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");
  const [copied, setCopied] = useState(false);

  const handleAiDescribe = async () => {
    if (!formData.description || formData.description.trim().length < 3) {
      toast.error("Scrivi almeno un appunto breve nella descrizione");
      return;
    }
    setAiBusy(true);
    try {
      const res = await api.aiAutoDescription(formData.description, formData.category, formData.client);
      setFormData((prev) => ({ ...prev, description: res.text }));
      toast.success("Descrizione espansa dall'AI");
    } catch (e) {
      toast.error("Errore AI");
    } finally {
      setAiBusy(false);
    }
  };

  const handleGenerateEmail = async () => {
    if (!formData.client) {
      toast.error("Serve un cliente");
      return;
    }
    setAiBusy(true);
    setEmailDraft("");
    try {
      const res = await api.aiGenerateEmail(task?.id, "professionale e cordiale");
      setEmailDraft(res.text);
    } catch (e) {
      toast.error("Errore generazione email. Salva prima l'attività se è nuova.");
    } finally {
      setAiBusy(false);
    }
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailDraft);
      setCopied(true);
      toast.success("Email copiata negli appunti");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Impossibile copiare");
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const cat = formData.category;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 modal-backdrop" data-testid="task-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto custom-scrollbar fade-in">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xl font-outfit font-bold text-slate-900" data-testid="task-modal-title">
            {task ? "Modifica Attività" : "Nuova Attività"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-modal-btn">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Cliente *</label>
              <input
                required value={formData.client} onChange={handleChange} name="client"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm"
                data-testid="task-client-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Categoria</label>
              <select
                value={formData.category} onChange={handleChange} name="category"
                className="w-full p-2.5 border border-slate-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm"
                data-testid="task-category-select"
              >
                {Object.values(CATEGORIES).map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Scadenza</label>
              <input
                type="date" value={formData.dueDate || ""} onChange={handleChange} name="dueDate"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm"
                data-testid="task-dueDate-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Telefono</label>
              <input
                value={formData.phone || ""} onChange={handleChange} name="phone"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm font-mono"
                data-testid="task-phone-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email</label>
              <input
                type="email" value={formData.email || ""} onChange={handleChange} name="email"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm"
                data-testid="task-email-input"
              />
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Descrizione</label>
                <button
                  type="button"
                  onClick={handleAiDescribe}
                  disabled={aiBusy}
                  className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-50 px-2 py-1 rounded transition disabled:opacity-50"
                  title="Espandi con AI"
                  data-testid="ai-expand-description-btn"
                >
                  {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Espandi con AI
                </button>
              </div>
              <textarea
                rows={3} value={formData.description || ""} onChange={handleChange} name="description"
                className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none mt-1 text-sm"
                data-testid="task-description-input"
                placeholder="Scrivi almeno 2 parole poi clicca 'Espandi con AI'..."
              />
            </div>

            {(cat === "CREDITI" || cat === "ACCONTI") && (
              <div className={`col-span-2 p-3 rounded-lg border space-y-3 ${
                cat === "CREDITI" ? "bg-emerald-50 border-emerald-200" : "bg-cyan-50 border-cyan-200"
              }`}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${cat === "CREDITI" ? "text-emerald-800" : "text-cyan-800"}`}>Totale (€)</label>
                    <input type="number" name="amount" value={formData.amount || 0} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" data-testid="task-amount-input" />
                  </div>
                  <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${cat === "CREDITI" ? "text-emerald-800" : "text-cyan-800"}`}>Pagato (€)</label>
                    <input type="number" name="paidAmount" value={formData.paidAmount || 0} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" data-testid="task-paidAmount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${cat === "CREDITI" ? "text-emerald-800" : "text-cyan-800"}`}>N. Fattura</label>
                    <input name="invoiceNumber" value={formData.invoiceNumber || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                  </div>
                  <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${cat === "CREDITI" ? "text-emerald-800" : "text-cyan-800"}`}>N. Proforma</label>
                    <input name="proformaNumber" value={formData.proformaNumber || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                  </div>
                  <div>
                    <label className={`text-[10px] font-bold uppercase tracking-wider ${cat === "CREDITI" ? "text-emerald-800" : "text-cyan-800"}`}>Data Doc.</label>
                    <input type="date" name="invoiceDate" value={formData.invoiceDate || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                  </div>
                </div>
              </div>
            )}

            {cat === "ORDINARE" && (
              <div className="col-span-2 grid grid-cols-2 gap-3 bg-orange-50 p-3 rounded-lg border border-orange-200">
                <div>
                  <label className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">N. Commissione</label>
                  <input name="orderNumber" value={formData.orderNumber || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">Data Commissione</label>
                  <input type="date" name="orderDate" value={formData.orderDate || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
              </div>
            )}

            {cat === "PAGARE" && (
              <div className="col-span-2 grid grid-cols-2 gap-3 bg-red-50 p-3 rounded-lg border border-red-200">
                <div>
                  <label className="text-[10px] font-bold text-red-700 uppercase tracking-wider">N. Preventivo/Fattura</label>
                  <input name="invoiceNumber" value={formData.invoiceNumber || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-red-700 uppercase tracking-wider">Data Documento</label>
                  <input type="date" name="invoiceDate" value={formData.invoiceDate || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
              </div>
            )}

            {cat === "RECALL" && (
              <div className="col-span-2 grid grid-cols-2 gap-3 bg-pink-50 p-3 rounded-lg border border-pink-200">
                <div>
                  <label className="text-[10px] font-bold text-pink-700 uppercase tracking-wider">N. Preventivo</label>
                  <input name="offerNumber" value={formData.offerNumber || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-pink-700 uppercase tracking-wider">Data Preventivo</label>
                  <input type="date" name="offerDate" value={formData.offerDate || ""} onChange={handleChange} className="w-full p-2 border border-slate-300 rounded mt-1 text-sm" />
                </div>
              </div>
            )}

            {/* Visibility selector - only editable by owner */}
            <div className="col-span-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider mb-2 block">Visibilità</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {[
                  { id: "private", label: "Privato", icon: Lock, desc: "Solo io" },
                  { id: "public", label: "Pubblico", icon: Globe, desc: "Tutti gli utenti" },
                  { id: "shared", label: "Condiviso", icon: Users, desc: "Utenti specifici" },
                ].map((v) => {
                  const active = formData.visibility === v.id;
                  const VIcon = v.icon;
                  return (
                    <button
                      key={v.id} type="button"
                      onClick={() => isOwner && setFormData((p) => ({ ...p, visibility: v.id, shared_with: v.id === "shared" ? p.shared_with : [] }))}
                      disabled={!isOwner}
                      className={`p-2 rounded-lg border-2 text-left transition ${
                        active ? "border-blue-600 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                      } ${!isOwner ? "opacity-60 cursor-not-allowed" : ""}`}
                      data-testid={`visibility-${v.id}`}
                    >
                      <VIcon size={15} className={active ? "text-blue-700" : "text-slate-500"} />
                      <div className={`text-xs font-bold mt-1 ${active ? "text-blue-700" : "text-slate-900"}`}>{v.label}</div>
                      <div className="text-[10px] text-slate-500">{v.desc}</div>
                    </button>
                  );
                })}
              </div>
              {formData.visibility === "shared" && (
                <div className="mt-2">
                  <label className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Condividi con</label>
                  <div className="max-h-32 overflow-y-auto custom-scrollbar mt-1 border border-slate-200 rounded-lg bg-white p-2 space-y-1">
                    {users.length === 0 && (
                      <div className="text-xs text-slate-400 text-center py-2">Nessun altro utente registrato ancora</div>
                    )}
                    {users.map((u) => {
                      const checked = (formData.shared_with || []).includes(u.id);
                      return (
                        <label key={u.id} className="flex items-center gap-2 text-sm p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                          <input
                            type="checkbox" checked={checked}
                            disabled={!isOwner}
                            onChange={(e) => {
                              const set = new Set(formData.shared_with || []);
                              if (e.target.checked) set.add(u.id); else set.delete(u.id);
                              setFormData((p) => ({ ...p, shared_with: Array.from(set) }));
                            }}
                            className="rounded"
                            data-testid={`share-user-${u.id}`}
                          />
                          <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">
                            {(u.name || u.email).slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-slate-900 truncate">{u.name || u.email.split("@")[0]}</div>
                            <div className="text-[10px] text-slate-500 truncate">{u.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {!isOwner && task && (
                <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  ⓘ Questo task è stato creato da un altro utente. Solo il proprietario può modificarne la visibilità.
                </div>
              )}
            </div>
          </div>

          {emailDraft && (
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 relative" data-testid="ai-email-draft">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider flex items-center gap-1">
                  <Sparkles size={11} /> Bozza email AI
                </span>
                <div className="flex gap-1">
                  <button
                    type="button" onClick={copyEmail}
                    className="flex items-center gap-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100 px-2 py-1 rounded"
                    data-testid="copy-email-btn"
                  >
                    {copied ? <><Check size={12} /> Copiato</> : <><Copy size={12} /> Copia</>}
                  </button>
                  <button
                    type="button" onClick={() => setEmailDraft("")}
                    className="text-blue-700 hover:bg-blue-100 p-1 rounded"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
              <pre className="text-xs text-slate-800 whitespace-pre-wrap font-ibm">{emailDraft}</pre>
            </div>
          )}

          <div className="flex justify-between items-center gap-3 pt-4 border-t border-slate-200">
            {task && (formData.category === "CREDITI" || formData.category === "ACCONTI" || formData.category === "RECALL" || formData.category === "PAGARE" || formData.category === "INVIARE_MAIL" || formData.category === "PROGETTARE") && (
              <button
                type="button" onClick={handleGenerateEmail} disabled={aiBusy}
                className="flex items-center gap-1.5 text-sm text-blue-700 border border-blue-300 hover:bg-blue-50 px-3 py-2 rounded-lg font-semibold disabled:opacity-50"
                data-testid="ai-generate-email-btn"
              >
                {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Genera Email AI
              </button>
            )}
            <div className="flex justify-end gap-3 ml-auto">
              <button
                type="button" onClick={onClose}
                className="px-5 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 font-medium text-sm"
                data-testid="task-cancel-btn"
              >
                Annulla
              </button>
              <button
                type="submit"
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2 rounded-lg font-semibold text-sm shadow-md"
                data-testid="task-save-btn"
              >
                Salva
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
